import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Loader2, FileText } from "lucide-react";
import api, { formatError } from "@/lib/api";
import WhatsAppLink from "@/components/admin/WhatsAppLink";
import ResendInvoiceButton from "@/components/admin/ResendInvoiceButton";
import CreditNoteButton from "@/components/admin/CreditNoteButton";
import ManualPayment from "@/components/admin/ManualPayment";

const STATUS_CLS = { pending: "bg-amber-300 text-slate-900", approved: "bg-green-600 text-white", rejected: "bg-red-600 text-white" };

export default function BillingReview() {
  const [subs, setSubs] = useState(null);
  const load = () => api.get("/admin/billing").then(({ data }) => setSubs(data.submissions)).catch(() => setSubs([]));
  useEffect(() => { load(); }, []);
  if (!subs) return <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>;
  return (
    <div className="space-y-3" data-testid="admin-billing-list">
      <ManualPayment onDone={load} />
      {!subs.length && <p className="text-slate-400 text-center py-8" data-testid="admin-billing-empty">No payment screenshots yet.</p>}
      {subs.map((s) => <Submission key={s.id} s={s} onDone={load} />)}
    </div>
  );
}

function Submission({ s, onDone }) {
  const [until, setUntil] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [note, setNote] = useState("");
  const [prices, setPrices] = useState(() => Object.fromEntries(s.items.map((it) => [it.child_id, it.price])));
  const [reason, setReason] = useState(s.discount_reason || "");
  const [busy, setBusy] = useState(false);
  const changed = s.items.some((it) => Number(prices[it.child_id]) !== it.price);
  const review = async (approve) => {
    setBusy(true);
    try {
      await api.post(`/admin/billing/${s.id}/review`, { approve, valid_until: until, note, prices: changed ? prices : {}, discount_reason: reason });
      toast.success(approve ? "Payment confirmed — subjects unlocked." : "Payment marked as not accepted.");
      onDone();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  return (
    <div className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-4 flex flex-col md:flex-row gap-4" data-testid={`admin-billing-${s.id}`}>
      <Proof s={s} />
      <div className="flex-1 text-sm space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${STATUS_CLS[s.status]}`} data-testid={`admin-billing-status-${s.id}`}>{s.status}</span>
          <span className="font-bold text-base">Rs {s.total}{s.items?.every((i) => i.plan === "holiday") ? " · HOLIDAY PACK" : s.items?.some((i) => i.plan === "holiday") ? " · incl. Holiday Pack" : "/month"}</span>
          <span className="text-slate-400">{s.parent_name} · {s.parent_email}</span>
          <WhatsAppLink number={s.parent_whatsapp} consentAccount={s.parent_consent_account} consentMarketing={s.parent_consent_marketing} name={s.parent_name} testId={`admin-billing-whatsapp-${s.id}`} />
          {s.uploaded_by_admin && <span className="text-xs rounded-full bg-blue-500/20 text-blue-200 px-2 py-0.5" data-testid={`admin-billing-by-admin-${s.id}`}>Added by admin</span>}
          <span className="ml-auto text-slate-500 text-xs">{new Date(s.created_at).toLocaleString()}</span>
        </div>
        {s.items.map((it) => (
          <p key={`${it.child_id}-${it.plan}`} className="text-slate-200 flex flex-wrap items-center gap-1.5">{it.child_name} (Grade {it.grade}) — {it.plan === "reading" ? `Reading kit ${(it.langs || []).map((l) => l.toUpperCase()).join(" + ")}` : it.subjects.join(", ")} ·
            {s.status === "pending" ? (
              <span className="inline-flex items-center gap-1">Rs <input type="number" min={0} value={prices[it.child_id]} data-testid={`admin-billing-price-${s.id}-${it.child_id}`}
                onChange={(e) => setPrices((p) => ({ ...p, [it.child_id]: e.target.value }))} className="w-20 bg-slate-900 border border-slate-600 rounded-lg px-2 py-0.5" />
                {Number(prices[it.child_id]) !== it.price && <span className="text-xs text-slate-400 line-through">Rs {it.price}</span>}</span>
            ) : <span>Rs {it.price}{it.list_price ? <span className="text-xs text-slate-400 line-through ml-1">Rs {it.list_price}</span> : null}</span>}
          </p>
        ))}
        {s.status === "pending" && changed && (
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for the new price (e.g. Promo Noël -20%) — shown on the invoice" data-testid={`admin-billing-discount-${s.id}`}
            className="w-full bg-slate-900 border border-amber-400 rounded-lg px-2 py-1" />
        )}
        {s.discount_reason && <p className="text-amber-200 text-xs">Promotion: {s.discount_reason}</p>}
        {s.status === "pending" ? (
          <div className="flex flex-wrap items-center gap-2 pt-2">
            {s.items.every((i) => i.plan === "holiday") ? (
              <span className="text-xs text-orange-300" data-testid={`admin-billing-holiday-note-${s.id}`}>Holiday access until the date in Promo codes → Holiday pack</span>
            ) : (
              <>
                <label className="text-xs text-slate-400">Access valid until</label>
                <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="bg-slate-900 border border-slate-600 rounded-lg px-2 py-1" data-testid={`admin-billing-until-${s.id}`} />
              </>
            )}
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to parent (optional)" className="flex-1 min-w-[160px] bg-slate-900 border border-slate-600 rounded-lg px-2 py-1" data-testid={`admin-billing-note-${s.id}`} />
            <button disabled={busy || (changed && reason.trim().length < 3)} onClick={() => review(true)} className="inline-flex items-center gap-1 rounded-full bg-green-600 px-3 py-1.5 font-semibold" data-testid={`admin-billing-approve-${s.id}`}><Check size={14} /> Confirm</button>
            <button disabled={busy} onClick={() => review(false)} className="inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1.5 font-semibold" data-testid={`admin-billing-reject-${s.id}`}><X size={14} /> Reject</button>
          </div>
        ) : (
          <>
            {s.note && <p className="text-slate-400">Note: {s.note}</p>}
            {s.status === "approved" && (
              <div className="flex items-center gap-2 pt-2">
                {s.invoice_no && <span className="text-xs text-slate-400 font-mono" data-testid={`admin-billing-invoice-${s.id}`}>{s.invoice_no}</span>}
                <ResendInvoiceButton sid={s.id} />
                <CreditNoteButton sub={s} onDone={onDone} />
                {s.credited > 0 && <span className="text-xs text-rose-300">Credited Rs {s.credited}</span>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Proof({ s }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/billing/proof/${s.id}`, { responseType: "blob" }).then(({ data }) => { u = URL.createObjectURL(data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [s.id]);
  if (!url) return <div className="w-40 h-40 rounded-xl bg-slate-900 flex items-center justify-center shrink-0"><Loader2 className="animate-spin" size={18} /></div>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="shrink-0" data-testid={`admin-billing-proof-${s.id}`}>
      {s.content_type === "application/pdf"
        ? <div className="w-40 h-40 rounded-xl bg-slate-900 flex flex-col items-center justify-center gap-2"><FileText size={28} /> <span className="text-xs">Open PDF</span></div>
        : <img src={url} alt="Payment screenshot" className="w-40 h-40 object-cover rounded-xl border border-slate-600" />}
    </a>
  );
}
